import { Trans, t } from '@lingui/macro'
import {
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'

import type { TrustedActor } from '@/core/models/trusted-actor.model'

import { ActorAvatar } from './ActorAvatar'

type Props = {
  actors: TrustedActor[]
  isLoading: boolean
  onEdit: (actor: TrustedActor) => void
  onDelete: (actor: TrustedActor) => void
}

const headCellSx = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase' as const,
  color: 'text.secondary',
}

export function DesktopTable({ actors, isLoading, onEdit, onDelete }: Props) {
  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
    >
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell sx={headCellSx}>
              <Trans>Name</Trans>
            </TableCell>
            <TableCell sx={headCellSx}>
              <Trans>Description</Trans>
            </TableCell>
            <TableCell sx={headCellSx}>
              <Trans>Added at</Trans>
            </TableCell>
            <TableCell align="right" sx={headCellSx}>
              <Trans>Actions</Trans>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                <Trans>Loading...</Trans>
              </TableCell>
            </TableRow>
          ) : actors.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                <Trans>No trusted actors configured</Trans>
              </TableCell>
            </TableRow>
          ) : (
            actors.map((actor) => (
              <TableRow key={actor._id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <ActorAvatar actor={actor} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {actor.name}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {actor.description || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {actor.addedAt ? new Date(actor.addedAt).toLocaleString() : '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                    <IconButton size="small" onClick={() => onEdit(actor)} aria-label={t`Edit trusted actor`}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => onDelete(actor)} aria-label={t`Remove trusted actor`}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
